import { useGoogleApiAutocompleteIntegration } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/GoogleApiAutocompleteIntegration";
import { getAddressOptions } from "@/apps/credit-line/components/CreditCard/ShippingAddress/composables/useSitiDataAutocompleteApi";
import { ref, Ref } from "vue";

export interface AutocompleteOption {
  key: string;
  text: string;
  original: Record<string, unknown>;
}

type GoogleComponents = ReturnType<
  typeof useGoogleApiAutocompleteIntegration
>["components"];
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    return new Promise((resolve) => {
      timeout = setTimeout(async () => {
        const result = await fn(...args);
        resolve(result);
      }, delay);
    });
  };
}

export function useAutocomplete(
  provider: "googlePlaces" | "sitiData",
  countryCode: string
): {
  initProvider: () => Promise<void>;
  selectOptions: Ref<AutocompleteOption[]>;
  autocompleteInput: Ref<string>;
  components: GoogleComponents;
  onAddressInput: (event: Event) => void;
} {
  // Google
  const { loadGoogleMapsScript, setupAutocomplete, components } =
    useGoogleApiAutocompleteIntegration();

  const selectOptions = ref<AutocompleteOption[]>([]);
  const autocompleteInput = ref("");

  const initProvider = async () => {
    if (provider === "googlePlaces") {
      await loadGoogleMapsScript();
      const input = document.getElementById("new_autocomplete");
      if (input) setupAutocomplete(input, countryCode);
    }
  };

  let debouncedGetOptions: (inputValue: string) => Promise<void> = (
    _inputValue: string
  ) => {
    selectOptions.value = [];
    return Promise.resolve();
  };

  if (provider === "sitiData") {
    debouncedGetOptions = debounce(
      async (inputValue: string): Promise<void> => {
        if (inputValue.length < 5) {
          selectOptions.value = [];
          return;
        }
        try {
          const options = await getAddressOptions(inputValue);
          selectOptions.value = options.map(
            (opt: Record<string, unknown>, idx: number) => {
              const direccion =
                typeof opt.direccion === "string"
                  ? capitalize(opt.direccion)
                  : "";
              const barrio =
                typeof opt.barrio === "string" ? capitalize(opt.barrio) : "";
              const ciudad =
                typeof opt.ciudad === "string" ? capitalize(opt.ciudad) : "";
              const departamento =
                typeof opt.departamento === "string"
                  ? capitalize(opt.departamento)
                  : "";
              return {
                key: idx.toString(),
                text: [direccion, barrio, ciudad, departamento]
                  .filter(Boolean)
                  .join(", "),
                original: { ...opt },
              };
            }
          );
        } catch (error) {
          selectOptions.value = [];
        }
        return;
      },
      600
    ) as (inputValue: string) => Promise<void>;
  }

  function capitalize(str: string) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const onAddressInput = (event: Event | string) => {
    if (provider !== "sitiData") return;
    const inputValue =
      typeof event === "string"
        ? event
        : (event.target as HTMLInputElement)?.value;
    autocompleteInput.value = inputValue;
    debouncedGetOptions(inputValue);
  };

  return {
    initProvider,
    selectOptions,
    autocompleteInput,
    components,
    onAddressInput,
  };
}
